import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { useState } from 'react';
import sparks from '@/assets/textures/sparks.svg';
import { usePrices } from '@/hooks/usePrices';
import { useEventTheme } from '@/providers/EventThemeProvider';
import { SuigarLogoMixedIcon } from '@/components/icons';
import { MoveBitAuditBadge } from '@/components/MoveBitAuditBadge';
import { SocialMenu } from './social-menu';
import { GAMES } from '@/constants/games';

type Props = {
	onOpenPlayResponsibly: () => void;
	onOpenProvablyFair: () => void;
	onOpenTerms: () => void;
	onOpenPrivacy: () => void;
};

interface FooterLinkProps {
	label: string;
	to?: string;
	href?: string;
	onClick?: () => void;
}

const footerLinkBaseClass = 'text-sm font-normal text-foreground/80 transition-colors hover:text-foreground text-left cursor-pointer';

function FooterLink({ label, to, href, onClick }: FooterLinkProps) {
	if (to) {
		return (
			<Link to={to} className={footerLinkBaseClass}>
				{label}
			</Link>
		);
	}
	if (href) {
		return (
			<a href={href} target="_blank" rel="noreferrer" className={footerLinkBaseClass}>
				{label}
			</a>
		);
	}
	return (
		<button type="button" onClick={onClick} className={footerLinkBaseClass}>
			{label}
		</button>
	);
}

export function CasinoFooter({ onOpenPlayResponsibly, onOpenProvablyFair, onOpenTerms, onOpenPrivacy }: Props) {
	const [expanded, setExpanded] = useState(false);
	const { event } = useEventTheme();
	const sideCapy = event.assets.suifrenImages.sideCapy;
	const sideBullshark = event.assets.suifrenImages.sideBullshark;
	const { data: priceData } = usePrices();
	const suiPrice = priceData?.sui?.usd ?? 0;
	const formattedSuiPrice = suiPrice ? suiPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
	const currentYear = new Date().getFullYear();

	const footerSections = [
		{
			title: 'Support',
			links: [
				{ label: 'Responsible Play', onClick: onOpenPlayResponsibly },
				{ label: 'Provably Fair', onClick: onOpenProvablyFair },
				{ label: 'Terms of Service', onClick: onOpenTerms },
				{ label: 'Privacy Policy', onClick: onOpenPrivacy },
				{ label: 'Brand Kit', to: '/brandkit' },
			],
		},
		{
			title: 'Games',
			links: GAMES.filter((game) => game.isGame).map((game) => ({
				label: game.name,
				to: game.link,
			})),
		},
		{
			title: 'Hub',
			links: [
				{ label: 'Slush Wallet Campaign', to: '/slush-wallet-campaign' },
				// { label: 'VIP Club', to: '/vip' },
				// { label: 'Affiliate', to: '/referral' },
				// { label: 'Rewards', to: '/rewards' },
				// { label: 'Roadmap', to: '/roadmap' },
				// { label: 'Token', to: '/token' },
				{ label: 'NFT', to: '/nft' },
				{ label: 'Profile', to: '/profile' },
				// { label: 'Help', href: 'https://suigar.zendesk.com' },
			],
		},
	];

	return (
		<footer className="relative w-full border-foreground bg-popover">
			{/* Collage background layer */}
			<div className="pointer-events-none absolute inset-x-0 bottom-0 select-none overflow-hidden">
				{/* subtle sparks */}
				<img
					src={sparks}
					alt=""
					aria-hidden="true"
					loading="lazy"
					decoding="async"
					className="absolute left-4 bottom-20 h-8 opacity-60 animate-pulse"
				/>
				{/* suifrens clipped at the bottom */}
				<img src={sideCapy} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute -bottom-2 -left-4 h-16 md:h-20" />
				<img
					src={sideBullshark}
					alt=""
					aria-hidden="true"
					loading="lazy"
					decoding="async"
					className="absolute -bottom-2.5 right-4 h-16 md:h-20 drop-shadow-[0_0_10px_rgba(0,0,0,0.3)]"
				/>
				{/* fade mask */}
				<div className="absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-popover to-transparent" />
			</div>

			<Card className="border-0 shadow-none bg-transparent">
				<CardContent className="px-4 py-8">
					<div className="grid mx-auto max-w-6xl gap-8 grid-cols-1 lg:grid-cols-3 justify-between">
						<section className="flex flex-col gap-3 max-w-xl lg:col-span-1">
							{/* Solid logo */}
							<div className="flex items-center gap-3 text-foreground">
								<SuigarLogoMixedIcon className="h-8 w-auto" aria-label="Suigar logo" />
							</div>
							<h2 className="text-xl">Crypto Online Casino on Sui</h2>
							<p className="text-sm text-muted-foreground">
								Suigar runs natively on the Sui blockchain, bringing transparent on-chain gameplay, fast finality, and low fees. Keep full custody of
								your funds while you play.
							</p>
							<MoveBitAuditBadge className="w-fit" />
							{expanded && (
								<div className="text-sm text-muted-foreground space-y-2">
									<p>
										We embrace decentralization: on-chain execution, verifiable randomness, and open mechanics. Our goal is to be the most
										decentralized casino experience.
									</p>
									<h3 className="font-semibold">Advantages</h3>
									<ul className="list-disc pl-5 space-y-1">
										<li>Anonymity and self-custody with wallets</li>
										<li>Worldwide access and fast settlement on Sui</li>
										<li>Provably-fair transparency for game results</li>
									</ul>
									<h3 className="font-semibold">Stay Safe</h3>
									<ul className="list-disc pl-5 space-y-1">
										<li>Set budgets and avoid chasing losses</li>
										<li>Verify fairness and review our Terms</li>
										<li>Seek help if play stops being fun</li>
									</ul>
									<h3 className="font-semibold">Games</h3>
									<p>Originals like Coinflip, Limbo, Plinko, Range and more, optimized for Sui’s speed, scalability, and low latency.</p>
								</div>
							)}
							<div>
								<Button variant="ghost" size="sm" className="px-0" onClick={() => setExpanded((v) => !v)}>
									{expanded ? 'Show less' : 'Show more'}
								</Button>
							</div>
						</section>

						{/* <section className="grid min-w-0 gap-4 grid-cols-3 col-span-2"> */}
						<section className="grid min-w-0 grid-cols-3 gap-6 lg:col-span-2">
								{footerSections.map((section) => (
									<div key={section.title} className="flex flex-col gap-2">
										<h3 className="mb-3 text-base font-extrabold font-sans">{section.title}</h3>
									<ul className="flex flex-col gap-2">
										{section.links.map((link) => (
											<li key={link.label}>
												<FooterLink {...link} />
											</li>
										))}
									</ul>
								</div>
							))}
						</section>
					</div>

					<div className="mt-6 mx-auto flex w-full max-w-6xl flex-col gap-4 rounded-2xl border border-foreground/15 bg-card/40 p-4 backdrop-blur md:flex-row md:items-center md:justify-between">
						<div className="max-w-xl space-y-1">
							<h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground font-sans">Stay connected</h3>
							<p className="text-xs text-foreground/70">Follow Suigar for contest announcements, new games, strategy content, and exclusive drops.</p>
						</div>
						<div className="flex flex-wrap gap-2">
							<SocialMenu size="sm" />
						</div>
					</div>
				</CardContent>
				<CardFooter className="border-t border-foreground/60 pt-4 flex items-center justify-center">
					<div className="w-full text-center text-xs font-semibold text-muted-foreground uppercase">
						Copyright ©{currentYear} SUIGAR ALL RIGHTS RESERVED. 1SUI=${formattedSuiPrice}
					</div>
				</CardFooter>
			</Card>
		</footer>
	);
}
